import { useMemo } from "react"
import { Link } from "react-router-dom"
import { ChevronRight, X } from "lucide-react"
import { useBidsStore } from "@/stores/bids-store"
import { useDashboardStore } from "@/stores/dashboard-store"

// Render **bold** spans from canonical/LLM insight copy as <strong>.
function InsightText({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g)
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-medium text-foreground">{part}</strong>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  )
}

// "Worth a look": the rule-engine insights, as a quiet second group under
// Home's "Needs you" rows. Flat rows, no cards — the work rows above lead.
// Hidden entirely at zero insights — no empty shell.
export function NeedsAttentionRow() {
  const insights = useDashboardStore((s) => s.insights)
  const totalCount = useDashboardStore((s) => s.insightsTotalCount)
  const dismissInsight = useDashboardStore((s) => s.dismissInsight)
  // Insights carry only the bid id; the title comes from the bids list Home
  // already loads, so two "237 days past deadline…" rows say which bid.
  const bids = useBidsStore((s) => s.bids)
  const bidTitles = useMemo(() => new Map(bids.map((b) => [b.id, b.title])), [bids])

  if (insights.length === 0) return null

  return (
    <section data-testid="worth-a-look" aria-labelledby="worth-a-look-title">
      <div className="mb-1 flex items-baseline gap-2">
        <h3 id="worth-a-look-title" className="text-meta">Worth a look</h3>
        {totalCount > insights.length && (
          <span className="text-meta ml-auto">
            {insights.length} of {totalCount}
          </span>
        )}
      </div>
      <ul className="divide-y divide-border">
        {insights.map((insight) => {
          const title = insight.relatedBidId ? bidTitles.get(insight.relatedBidId) : undefined
          // Some insight copy already names the bid; don't say it twice.
          const bidTitle = title && !insight.message.includes(title) ? title : undefined
          return (
          <li key={insight.id} className="group flex items-start gap-3 py-2" data-testid="insight-row">
            <p className="min-w-0 flex-1 text-[13px] leading-relaxed text-muted-foreground">
              {bidTitle && (
                <>
                  <span
                    className="inline-block max-w-[50%] truncate align-bottom font-medium text-foreground"
                    title={bidTitle}
                    data-testid="insight-bid-title"
                  >
                    {bidTitle}
                  </span>
                  <span aria-hidden> · </span>
                </>
              )}
              <InsightText text={insight.message} />
            </p>
            <Link
              to={insight.actionUrl}
              className="mt-0.5 inline-flex shrink-0 items-center gap-0.5 text-[12px] text-[hsl(var(--link))] hover:underline"
            >
              {insight.actionLabel}
              <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </Link>
            <button
              type="button"
              aria-label="Dismiss insight"
              onClick={() => void dismissInsight(insight.id, insight.ruleId)}
              className="mt-0.5 shrink-0 rounded-md p-0.5 text-subtle transition-colors hover:bg-muted hover:text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </li>
          )
        })}
      </ul>
    </section>
  )
}
